import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import GridLoader from '@hobs/components/Grid/GridLoader';
import { H2 } from '@hobs/components/Headings';
import { Grid } from '@progress/kendo-react-grid';
import { CellDateTime } from '@hobs/components/Grid/CellDate';
import { createLoadingSelector } from '@hobs/utils/loading';
import Panel from '@hobs/components/Panel';
import Columns from '@hobs/components/Grid/Columns';
import { queryOData, resetOData } from '@hobs/actions/odata';
import { getAuditLogGrid } from '@hobs/actions/gridSelections';
import { PAGE_SIZES } from '@hobs/utils/constants';

import AuditLogGridItem from './AuditLogGridItem';

import './AuditLogGrid.scss';

class AuditLogGrid extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      auditLog: null,
      dataState: {
        take: 20,
        skip: 0,
        sort: [{ field: 'CreatedDate', dir: 'desc' }]
      },
      customFilters: {}
    };
  }

  componentDidMount() {
    this.props.getAuditLogGrid();
    this.loadODataQuery();
  }

  componentWillUnmount() {
    this.props.resetOData();
  }

  getDefaultColumns() {
    const { t } = this.props;
    return {
      CreatedDate: {
        title: t('Date'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'CreatedDate',
        cell: CellDateTime
      },
      PhaseTypeId: {
        title: t('Phase'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getPhaseTypeFilterValues()
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{this.getPhaseTypeValue(dataItem.PhaseTypeId)}</td>;
        }
      },
      ActionTypeId: {
        title: t('Action'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getActionTypeFilterValues()
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.ActionName}</td>;
        }
      },
      UserFullName: {
        title: t('User'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Fields: {
        title: t('Fields'),
        columnMenu: true,
        default: true,
        visible: true
      }
    };
  }

  getActionTypeValue = actionTypeId => {
    const { t, auditLogGrid } = this.props;

    if (auditLogGrid && auditLogGrid.actions) {
      const actionType = auditLogGrid.actions.find(action => {
        return action.id === actionTypeId;
      });

      if (actionType) {
        return t(actionType.title);
      }
    }

    return <></>;
  };

  getPhaseTypeValue = phaseTypeId => {
    const { t, auditLogGrid } = this.props;

    if (auditLogGrid && auditLogGrid.phases) {
      const phaseType = auditLogGrid.phases.find(phase => {
        return phase.id === phaseTypeId;
      });

      if (phaseType) {
        return t(phaseType.title);
      }
    }

    return <></>;
  };

  getActionTypeFilterValues() {
    const { t, auditLogGrid } = this.props;

    if (auditLogGrid && auditLogGrid.actions) {
      return auditLogGrid.actions.map(filterValue => {
        return {
          name: t(filterValue.title),
          value: filterValue.id
        };
      });
    }

    return [];
  }

  getPhaseTypeFilterValues() {
    const { t, auditLogGrid } = this.props;

    if (auditLogGrid && auditLogGrid.phases) {
      return auditLogGrid.phases.map(filterValue => {
        return {
          name: t(filterValue.title),
          value: filterValue.id
        };
      });
    }

    return [];
  }

  dataStateChange = e => {
    this.setState(
      {
        dataState: e.dataState
      },
      () => {
        this.loadODataQuery();
      }
    );
  };

  getDataState() {
    let filters = [];

    if (Object.keys(this.state.customFilters).length > 0) {
      Object.values(this.state.customFilters).map(filter => {
        if (filter) {
          filters.push(filter);
        }
      });
    }

    const dataState = {
      ...this.state.dataState
    };

    if (
      this.state.dataState.filter &&
      this.state.dataState.filter.filters &&
      this.state.dataState.filter.filters.length > 0
    ) {
      filters = filters.concat(this.state.dataState.filter.filters);
    }

    if (filters.length > 0) {
      dataState.filter = {
        logic: 'and',
        filters
      };
    }

    return dataState;
  }

  loadODataQuery = () => {
    this.props.queryOData(
      `AuditLogQuery(${this.props.match.params.serviceRequestId})`,
      this.getDataState()
    );
  };

  onDetailsClose = () => {
    this.setState({
      auditLog: null
    });
  };

  render() {
    const { t } = this.props;
    const { auditLog } = this.state;

    return (
      <>
        <Panel>
          <div className="auditLog-view">
            <div className="auditLog-view--items">
              <H2>{t('Service request history')}</H2>
              <Grid
                sortable={true}
                pageable={{
                  pageSizes: PAGE_SIZES
                }}
                {...this.state.dataState}
                {...this.props.oData}
                onDataStateChange={this.dataStateChange}
                onRowClick={({ dataItem }) => {
                  this.setState({
                    auditLog: dataItem
                  });
                }}
                selectedField="selected"
                data={this.props.oData.data.map(item => ({
                  ...item,
                  selected: auditLog && item.AuditLogId === auditLog.AuditLogId
                }))}
              >
                {Columns({
                  columns: this.getDefaultColumns(),
                  dataState: this.state.dataState
                })}
              </Grid>
              {this.props.isLoading && <GridLoader />}
            </div>
            {auditLog && (
              <AuditLogGridItem
                auditLog={auditLog}
                onClose={this.onDetailsClose}
                getActionTypeValue={this.getActionTypeValue}
                getPhaseTypeValue={this.getPhaseTypeValue}
              />
            )}
          </div>
        </Panel>
      </>
    );
  }
}

AuditLogGrid.propTypes = {
  isLoading: PropTypes.bool,
  match: PropTypes.object,
  oData: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  loadODataQuery: PropTypes.func,
  t: PropTypes.func,
  getAuditLogGrid: PropTypes.func,
  auditLogGrid: PropTypes.object
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state),
  oData: state.odata,
  auditLogGrid: state.gridSelections.auditLogGrid
});

const mapDispatchToProps = {
  queryOData,
  resetOData,
  getAuditLogGrid
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(AuditLogGrid))
);
